import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  User,
  MapPin,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  Heart,
  GraduationCap,
  Camera,
  Video,
  FileText,
  Users,
  TrendingUp,
  Shield,
  Star,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import {
  useGetAllChildrenQuery,
  useDeleteChildMutation,
  useVerifyChildMutation
} from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';
import ProfilePreviewModal from '../../components/admin/ProfilePreviewModal';
import UserAvatar from '../../components/common/UserAvatar';

const ChildSponsorship: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedChildren, setSelectedChildren] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [childToDelete, setChildToDelete] = useState<any>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // API hooks - Get children with pagination
  const { data: childrenResponse, isLoading, error, refetch } = useGetAllChildrenQuery({
    page: currentPage,
    limit: itemsPerPage,
    search: searchQuery || undefined,
    // Only send status filter for actual status values, not verification filters
    status: (statusFilter !== 'all' && statusFilter !== 'verified' && statusFilter !== 'pending')
      ? statusFilter
      : undefined
  });

  const [deleteChild] = useDeleteChildMutation();
  const [verifyChild] = useVerifyChildMutation();

  const children = childrenResponse?.data?.children || [];
  const pagination = childrenResponse?.data?.pagination;

  // Calculate pagination info
  const totalPages = pagination?.total_pages || 1;
  const totalChildren = pagination?.total_children || children.length;

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  // Refetch when search query, page, or items per page changes
  useEffect(() => {
    refetch();
  }, [searchQuery, currentPage, itemsPerPage, refetch]);

  // Filter and sort children using useMemo for performance
  const filteredChildren = useMemo(() => {
    return children
      .filter(child => {
        // Add defensive checks for required properties
        if (!child || !child.name) return false;

        const matchesSearch = child.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (child.location && child.location.toLowerCase().includes(searchQuery.toLowerCase()));

        let matchesStatus = true;
        if (statusFilter === 'all') {
          matchesStatus = true;
        } else if (statusFilter === 'verified') {
          // Show only verified children
          matchesStatus = child.is_verified === true;
        } else if (statusFilter === 'pending') {
          // Show only unverified/pending children
          matchesStatus = child.is_verified === false;
        } else if (statusFilter === 'available') {
          // Check if child is available for sponsorship (not sponsored and not graduated)
          matchesStatus = child.status === 'available' || (!child.status || child.status === 'available');
        } else if (statusFilter === 'sponsored') {
          matchesStatus = child.status === 'sponsored' && !child.is_sponsorship_paused;
        } else if (statusFilter === 'paused') {
          matchesStatus = child.is_sponsorship_paused || child.current_sponsorship_status === 'paused';
        } else if (statusFilter === 'graduated') {
          matchesStatus = child.status === 'graduated';
        } else {
          matchesStatus = child.status === statusFilter;
        }

        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'age':
            comparison = a.age - b.age;
            break;
          case 'createdAt':
            comparison = new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime();
            break;
          case 'totalRaised':
            comparison = (a.total_raised || 0) - (b.total_raised || 0);
            break;
          default:
            comparison = 0;
        }
        return sortOrder === 'desc' ? -comparison : comparison;
      });
  }, [children, searchQuery, statusFilter, sortBy, sortOrder]);

  // Debug: Log the data structure and counts
  if (children.length > 0 && process.env.NODE_ENV === 'development') {
    console.log('Children data:', children);
    console.log('Status counts:', {
      total: children.length,
      available: children.filter(c => c.status === 'available' || !c.status).length,
      sponsored: children.filter(c => c.status === 'sponsored').length,
      graduated: children.filter(c => c.status === 'graduated').length,
      verified: children.filter(c => c.is_verified === true).length,
      pending: children.filter(c => c.is_verified === false).length
    });
    console.log('Current filter:', statusFilter);
    console.log('Filtered children count:', filteredChildren.length);
    console.log('Verification details:', children.map(c => ({
      name: c.name,
      is_verified: c.is_verified,
      status: c.status
    })));
  }

  const handleDeleteChild = (child: any) => {
    setChildToDelete(child);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteChild = async () => {
    if (!childToDelete) return;

    try {
      const response = await deleteChild({ child_id: childToDelete.child_id }).unwrap();

      // Check if the response indicates success
      if (response.success === false) {
        // Backend returned success: false, show the error message
        const errorMessage = response.message || 'Failed to delete child';
        setDeleteError(errorMessage);
        return; // Don't close modal, show error
      }

      // Success case
      toast.success('Child deleted successfully');
      refetch();
      setIsDeleteModalOpen(false);
      setChildToDelete(null);
      setDeleteError(null);
    } catch (error: any) {
      // Handle network errors or other exceptions
      const errorMessage = error?.data?.message || 'Failed to delete child';
      setDeleteError(errorMessage);
      // Don't close modal on error, show the error message instead
    }
  };

  const handleVerifyChild = async (childId: number) => {
    try {
      const child = children.find(c => c.child_id === childId);
      if (child) {
        await verifyChild({
          child_id: childId,
          verified: !child.is_verified
        }).unwrap();
        toast.success('Child verification updated successfully');
        refetch();
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update child verification');
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const getStatusBadge = (child: any) => {
    // Check if sponsorship is paused first
    if (child.is_sponsorship_paused || child.current_sponsorship_status === 'paused') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Paused
        </span>
      );
    }

    // Then check the main status
    switch (child.status) {
      case 'available':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Clock className="w-3 h-3 mr-1" />
            Available
          </span>
        );
      case 'sponsored':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Heart className="w-3 h-3 mr-1" />
            Sponsored
          </span>
        );
      case 'graduated':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <GraduationCap className="w-3 h-3 mr-1" />
            Graduated
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Clock className="w-3 h-3 mr-1" />
            Available
          </span>
        );
    }
  };

  const getVerificationBadge = (verified: boolean) => {
    return verified ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Verified
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <AlertCircle className="w-3 h-3 mr-1" />
        Pending
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const calculateProgress = (raised: number, target: number) => {
    return Math.min((raised / target) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Child Sponsorship</h1>
              <p className="text-gray-600">Manage children available for sponsorship and track their progress</p>
            </div>
            <button
              onClick={() => navigate('/admin/child-sponsorship/create')}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Child
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Children</p>
                <p className="text-2xl font-bold text-gray-900">{children.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Heart className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Sponsored</p>
                <p className="text-2xl font-bold text-gray-900">
                  {children.filter(c => c.status === 'sponsored' && !c.is_sponsorship_paused).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Paused</p>
                <p className="text-2xl font-bold text-gray-900">
                  {children.filter(c => c.is_sponsorship_paused || c.current_sponsorship_status === 'paused').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Available</p>
                <p className="text-2xl font-bold text-gray-900">
                  {children.filter(c => c.status === 'available' || !c.status).length}
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search children by name or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="available">Available</option>
                  <option value="sponsored">Sponsored</option>
                  <option value="paused">Paused</option>
                  <option value="graduated">Graduated</option>
                  <option value="verified">Verified Only</option>
                  <option value="pending">Pending Verification</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="createdAt">Sort by Date</option>
                  <option value="name">Sort by Name</option>
                  <option value="age">Sort by Age</option>
                  <option value="totalRaised">Sort by Amount Raised</option>
                </select>

                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Children Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Children</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>Showing {children.length} of {totalChildren} children</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading children: {error?.data?.message || 'Unknown error'}</p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Child</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Study</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified or not</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredChildren.map((child) => {
                    // Add defensive checks for required properties
                    if (!child || !child.child_id) {
                      return null;
                    }

                    return (
                      <tr key={child.child_id} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => {
                                setSelectedChild(child);
                                setIsProfilePreviewOpen(true);
                              }}
                              title="Click to view profile preview"
                            >
                              <UserAvatar 
                                src={child.profile_image} 
                                name={child.name} 
                                size="sm" 
                              />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{child.name}</div>
                              <div className="text-sm text-gray-500">ID: {child.child_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">{child.age}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">
                              {child.location}
                              {(() => {
                                const countryCode = (child.country_code === 'NG' && child.currency_code && child.currency_code !== 'NGN')
                                  ? 'IN' // Simplified for common Indian cases as reported by user
                                  : (child.country_code || (child.currency_code === 'INR' ? 'IN' : ''));
                                
                                return countryCode ? ` (${countryCode})` : '';
                              })()}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <GraduationCap className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900">{child.education_stage}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(child)}
                        </td>
                        <td className="px-6 py-4">
                          {getVerificationBadge(child.is_verified)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500">
                              {child.created_at ? formatDateUtil(child.created_at) : 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => navigate(`/admin/child-sponsorship/${child.child_id}`)}
                              className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                              title="View details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => navigate(`/admin/child-sponsorship/edit/${child.child_id}`)}
                              className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                              title="Edit child"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleVerifyChild(child.child_id)}
                              className={`p-2 rounded-lg transition-all duration-200 ${child.is_verified
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-yellow-600 hover:bg-yellow-50'
                                }`}
                              title={child.is_verified ? 'Unverify child' : 'Verify child'}
                            >
                              <Shield className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteChild(child)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                              title="Delete child"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!isLoading && !error && filteredChildren.length === 0 && (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No children found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first child.'}
            </p>
            <button
              onClick={() => navigate('/admin/child-sponsorship/create')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Child
            </button>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && totalPages > 1 && (
          <div className="bg-white rounded-lg shadow border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing page {currentPage} of {totalPages} ({totalChildren} total children)
              </div>

              <div className="flex items-center space-x-2">
                {/* First Page */}
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="First page"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>

                {/* Previous Page */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                {/* Next Page */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                {/* Last Page */}
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Last page"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Profile Preview Modal */}
        {selectedChild && (
          <ProfilePreviewModal
            isOpen={isProfilePreviewOpen}
            onClose={() => {
              setIsProfilePreviewOpen(false);
              setSelectedChild(null);
            }}
            child={selectedChild}
          />
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && childToDelete && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Delete Child</h3>
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setChildToDelete(null);
                      setDeleteError(null);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {deleteError ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-red-600 font-medium">Cannot delete child</p>
                      <p className="text-sm text-gray-600 mt-1">{deleteError}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-gray-600">
                        Are you sure you want to delete{' '}
                        <span className="font-semibold text-gray-900">{childToDelete.name}</span>?
                      </p>
                      <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setChildToDelete(null);
                      setDeleteError(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                  >
                    {deleteError ? 'Close' : 'Cancel'}
                  </button>
                  {!deleteError && (
                    <button
                      onClick={confirmDeleteChild}
                      className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Delete Child
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChildSponsorship;
